import React, { useState, useEffect, useContext } from 'react';
import ConfigService from '../../services/ConfigService';
import SearchBox from '../components/SearchBox';
import { HelpCircle } from 'lucide-react';
import TestTubeRow, { TestTube, TestTubes } from './TestTubeRow';
import AppsHeader from '../components/AppsHeader';


const TestTubesComponent: React.FC<{ hospital: string }> = ({ hospital }) => {
  const [testTubes, setTestTubes] = useState<TestTube[]>([]);
  const [substitutionTubes, setSubstitutionTubes] = useState<string>('');
  const [filteredTubes, setFilteredTubes] = useState<TestTube[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showBalloon, setShowBalloon] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Reference to the balloon for click outside handling
  const balloonRef = useState<React.RefObject<HTMLDivElement>>(React.createRef())[0];
  const linkRef = useState<React.RefObject<HTMLButtonElement>>(React.createRef())[0];

  useEffect(() => {
    const fetchTestTubes = async () => {
      try {
        setIsLoading(true);
        setError(null);
        // Use the ConfigService to fetch data from the API
        const data = await ConfigService.getConfig('TEST_TUBES', 'test-tubes', hospital);
        setSubstitutionTubes(data?.data?.substitutesTubes || '');
        setTestTubes(data?.data?.tubes || []);
        setFilteredTubes(data?.data?.tubes || []);
      } catch (err) {
        console.error('Failed to fetch test tubes data:', err);
        setError('Failed to load test tubes data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestTubes();
  }, [hospital]);

  // Effect for handling clicks outside the balloon and ESC key press
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showBalloon &&
        balloonRef.current &&
        !balloonRef.current.contains(event.target as Node) &&
        linkRef.current &&
        !linkRef.current.contains(event.target as Node)
      ) {
        setShowBalloon(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (showBalloon && event.key === 'Escape') {
        setShowBalloon(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showBalloon, balloonRef, linkRef]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredTubes(testTubes);
      return;
    }

    const lowercasedSearch = searchTerm.toLowerCase();
    const filtered = testTubes.filter(tube => {
      // Check if any keyword includes the search term
      const keywordsMatch = Array.isArray(tube.keywords) && tube.keywords.some(keyword => keyword.toLowerCase().includes(lowercasedSearch));
      // Check if any hidden keyword includes the search term
      const hiddenKeywordsMatch = Array.isArray(tube.hiddenKeywords) && tube.hiddenKeywords.some(hiddenKeyword => hiddenKeyword.toLowerCase().includes(lowercasedSearch));

      return (
        (tube.mainTitle && tube.mainTitle.toLowerCase().includes(lowercasedSearch)) ||
        (tube.shortName && tube.shortName.toLowerCase().includes(lowercasedSearch)) ||
        keywordsMatch ||
        hiddenKeywordsMatch || // Add hiddenKeywords check here
        (tube.notes && tube.notes.toLowerCase().includes(lowercasedSearch))
      );
    });

    setFilteredTubes(filtered);
  }, [searchTerm, testTubes]);


  const handleSearch = (value: string) => {
    setSearchTerm(value);
  };

  const toggleExpand = (index: number) => {
    setExpandedId(expandedId === index ? null : index);
  };

  const toggleBalloon = () => {
    setShowBalloon(!showBalloon);
  };

  /**
   * Parses a string for <b> and <br> tags and converts them to React elements
   * @param text The text to parse
   * @returns An array of React elements
   */
  const parseHtmlTags = (text: string): React.ReactNode[] => {
    if (!text) return [];
    
    // Split the text by <br> tags
    const lines = text.split(/<br\s*\/?>/i);
    
    // Process each line for <b> tags
    return lines.map((line, lineIndex) => {
      // Find all <b> tags in the line
      const boldParts: React.ReactNode[] = [];
      let lastIndex = 0;
      let match;
      
      // Regular expression to match <b>...</b> patterns
      const boldRegex = /<b>(.*?)<\/b>/gi;
      
      while ((match = boldRegex.exec(line)) !== null) {
        // Add text before the <b> tag
        if (match.index > lastIndex) {
          boldParts.push(line.substring(lastIndex, match.index));
        }
        
        // Add the bold text
        boldParts.push(<b key={`bold-${lineIndex}-${match.index}`}>{match[1]}</b>);
        
        lastIndex = match.index + match[0].length;
      }
      
      // Add any remaining text after the last <b> tag
      if (lastIndex < line.length) {
        boldParts.push(line.substring(lastIndex));
      }
      
      // If no <b> tags were found, just return the line
      if (boldParts.length === 0) {
        boldParts.push(line);
      }
      
      // Return the line with a <br> after it (except for the last line)
      return (
        <React.Fragment key={`line-${lineIndex}`}>
          {boldParts}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="test-tubes container main-content" style={{
      container: 'test-tubes',
    }}>
      <div >
        <AppsHeader 
          title={hospital === "emek" ? "Emek Labs" : "בדיקות מעבדה"}
        />

        <div style={{ marginBottom: '10px', position: 'relative' }}>
          <SearchBox
            value={searchTerm}
            onChange={handleSearch}
            placeholder="חיפוש בדיקות מעבדה..."
          />

          <div style={{ display: 'flex', justifyContent: 'flex-start', position: 'relative' }}>
            <button
              ref={linkRef} // Using your existing linkRef
              onClick={toggleBalloon}
              style={{
                display: 'flex',
                alignItems: 'center',
                fontWeight: 'bold',
                padding: '0px 5px',
                color: '#103C6E',
                backgroundColor: 'transparent',
                border: 'none',
                borderRadius: '4px',
                margin: '0px',
                cursor: 'pointer'
              }}
            >
              מבחנות תחליפיות
              <HelpCircle style={{ marginRight: '4px' }} size={16} />
            </button>

            {showBalloon && (
              <div
                ref={balloonRef}
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: '0',
                  zIndex: 10,
                  width: '350px',
                  padding: '12px',
                  backgroundColor: '#EEFFFD',
                  borderRadius: '8px',
                  border: '1px solid #96AAC6',
                  marginTop: '8px',
                  textAlign: 'right',
                  direction: 'rtl',
                  lineHeight: '1.1'
                }}
              >
                <div className="info-container" style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div style={{
                    minWidth: '16px',
                    height: '16px',
                    backgroundColor: '#103C6E',
                    color: 'white',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: '8px',
                    fontWeight: 'bold',
                    fontSize: '10px'
                  }}>
                    i
                  </div>
                  <p style={{ margin: 0 }}>
                    <b>מבחנות תחליפיות</b><br />
                      {parseHtmlTags(substitutionTubes)}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column'}}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <p>טוען נתונים...</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', color: 'red', padding: '20px' }}>
              <p>{error}</p>
            </div>
          ) : filteredTubes.length === 0 ? (
            <div style={{
              textAlign: 'center',
              backgroundColor: '#f9fafb',
              borderRadius: '8px'
              }}>
              <p style={{ fontSize: '1.125rem', color: '#4b5563' }}>
                לא נמצאו תוצאות התואמות את החיפוש.
              </p>
            </div>
          ) : (
            filteredTubes.map((tube, index) => (
              <TestTubeRow
                key={index}
                tube={tube}
                index={index}
                expandedId={expandedId}
                toggleExpand={toggleExpand}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TestTubesComponent;
